import { useState, useEffect } from 'react'
import { lockScroll, unlockScroll } from '../../lib/scrollLock'
import { useInjuryDetail } from '../../hooks/useInjuries'
import { getTeamLogoUrl, getTeamLogoFallbackUrl } from '../../lib/teamLogos'
import LoadingSpinner from '../ui/LoadingSpinner'
import InjuryBadge from '../ui/InjuryBadge'
import GamePreview from './GamePreview'
import ProbablePitchers from './ProbablePitchers'
import { useGameIntel } from '../../hooks/useGames'

function TeamLogo({ team, sportKey }) {
  const [src, setSrc] = useState(() => getTeamLogoUrl(team, sportKey))
  const [hidden, setHidden] = useState(false)
  if (!src || hidden) return null
  return <img src={src} alt="" className="w-12 h-12 object-contain mx-auto mb-1" onError={() => {
    const fallback = getTeamLogoFallbackUrl(team, sportKey)
    if (fallback && fallback !== src) setSrc(fallback)
    else setHidden(true)
  }} />
}

const STATUS_STYLES = {
  Out: 'text-incorrect',
  Doubtful: 'text-orange-400',
  Questionable: 'text-yellow-400',
  Probable: 'text-text-muted',
  'Day-To-Day': 'text-text-muted',
}

const STATUS_LABELS = {
  Out: 'Out',
  Doubtful: 'Doubt',
  Questionable: 'Ques',
  Probable: 'Prob',
  'Day-To-Day': 'DTD',
}

const BORDER_COLORS = {
  Out: 'border-incorrect',
  Doubtful: 'border-orange-400',
  Questionable: 'border-yellow-400',
  Probable: 'border-text-muted',
  'Day-To-Day': 'border-text-muted',
}

// Primary team colors (official brand colors)
const TEAM_COLORS = {
  // NBA
  'Atlanta Hawks': '#E03A3E',
  'Boston Celtics': '#007A33',
  'Brooklyn Nets': '#000000',
  'Charlotte Hornets': '#1D1160',
  'Chicago Bulls': '#CE1141',
  'Cleveland Cavaliers': '#860038',
  'Dallas Mavericks': '#00538C',
  'Denver Nuggets': '#0E2240',
  'Detroit Pistons': '#C8102E',
  'Golden State Warriors': '#1D428A',
  'Houston Rockets': '#CE1141',
  'Indiana Pacers': '#002D62',
  'Los Angeles Clippers': '#C8102E',
  'Los Angeles Lakers': '#552583',
  'Memphis Grizzlies': '#5D76A9',
  'Miami Heat': '#98002E',
  'Milwaukee Bucks': '#00471B',
  'Minnesota Timberwolves': '#0C2340',
  'New Orleans Pelicans': '#0C2340',
  'New York Knicks': '#F58426',
  'Oklahoma City Thunder': '#007AC1',
  'Orlando Magic': '#0077C0',
  'Philadelphia 76ers': '#006BB6',
  'Phoenix Suns': '#E56020',
  'Portland Trail Blazers': '#E03A3E',
  'Sacramento Kings': '#5A2D81',
  'San Antonio Spurs': '#C4CED4',
  'Toronto Raptors': '#CE1141',
  'Utah Jazz': '#002B5C',
  'Washington Wizards': '#002B5C',
  // NFL
  'Arizona Cardinals': '#97233F',
  'Atlanta Falcons': '#A71930',
  'Baltimore Ravens': '#241773',
  'Buffalo Bills': '#00338D',
  'Carolina Panthers': '#0085CA',
  'Chicago Bears': '#0B162A',
  'Cincinnati Bengals': '#FB4F14',
  'Cleveland Browns': '#FF3C00',
  'Dallas Cowboys': '#003594',
  'Denver Broncos': '#FB4F14',
  'Detroit Lions': '#0076B6',
  'Green Bay Packers': '#203731',
  'Houston Texans': '#03202F',
  'Indianapolis Colts': '#002C5F',
  'Jacksonville Jaguars': '#006778',
  'Kansas City Chiefs': '#E31837',
  'Las Vegas Raiders': '#A5ACAF',
  'Los Angeles Chargers': '#0080C6',
  'Los Angeles Rams': '#003594',
  'Miami Dolphins': '#008E97',
  'Minnesota Vikings': '#4F2683',
  'New England Patriots': '#002244',
  'New Orleans Saints': '#D3BC8D',
  'New York Giants': '#0B2265',
  'New York Jets': '#125740',
  'Philadelphia Eagles': '#004C54',
  'Pittsburgh Steelers': '#FFB612',
  'San Francisco 49ers': '#AA0000',
  'Seattle Seahawks': '#002244',
  'Tampa Bay Buccaneers': '#D50A0A',
  'Tennessee Titans': '#0C2340',
  'Washington Commanders': '#5A1414',
  // WNBA
  'Atlanta Dream': '#E31837',
  'Chicago Sky': '#418FDE',
  'Connecticut Sun': '#F05023',
  'Dallas Wings': '#C4D600',
  'Golden State Valkyries': '#1D428A',
  'Indiana Fever': '#002D62',
  'Las Vegas Aces': '#A7A8AA',
  'Los Angeles Sparks': '#552583',
  'Minnesota Lynx': '#0C2340',
  'New York Liberty': '#6ECEB2',
  'Phoenix Mercury': '#CB6015',
  'Portland Fire': '#C8102E',
  'Seattle Storm': '#2C5234',
  'Toronto Tempo': '#14B5A0',
  'Washington Mystics': '#C8102E',
}

function InjuryRow({ inj }) {
  const [expanded, setExpanded] = useState(false)

  return (
    <div
      className={`border-l-2 ${BORDER_COLORS[inj.status] || 'border-text-muted'} pl-2 py-0.5${inj.detail ? ' cursor-pointer' : ''}`}
      onClick={() => inj.detail && setExpanded((e) => !e)}
    >
      <div className="flex items-center gap-1.5 text-sm">
        <span className="text-text-muted text-xs w-6 shrink-0">{inj.position}</span>
        <span className="text-text-primary truncate">{inj.shortName}</span>
        <span className={`text-xs font-semibold shrink-0 ${STATUS_STYLES[inj.status] || 'text-text-muted'}`}>
          {STATUS_LABELS[inj.status] || inj.status}
        </span>
      </div>
      {inj.detail && (
        <div className={`text-xs text-text-muted ml-[1.875rem] ${expanded ? '' : 'truncate'}`}>{inj.detail}</div>
      )}
    </div>
  )
}

function TeamSection({ teamName, starters, injuries, sportKey }) {
  const hasInjuries = injuries?.length > 0

  // Build injury status map by player name for cross-referencing with starters
  const injuryMap = {}
  for (const inj of injuries || []) {
    injuryMap[inj.name] = inj.status
    injuryMap[inj.shortName] = inj.status
  }

  // Build today's starters: promote next man up when starter is Out/Doubtful
  // Skip players already used at a previous position
  const usedPlayers = new Set()
  const todayStarters = (starters || []).map((s) => {
    const depth = s.depth || [{ name: s.name, shortName: s.shortName }]
    for (const player of depth) {
      if (usedPlayers.has(player.name)) continue
      const status = injuryMap[player.name] || injuryMap[player.shortName]
      // Anyone who cannot take the field gets promoted past, not just Out and
      // Doubtful. IR, PUP, suspended and DNR were all falling through and
      // being rendered as starters — which is how a PUP running back kept his
      // RB1 slot. Questionable deliberately stays: he is expected to play, so
      // he holds the slot and carries a badge.
      // Suspensions match by prefix — Sleeper also writes 'Suspension'.
      const st = String(status || '').trim().toLowerCase()
      if (st.startsWith('sus') || ['out', 'doubtful', 'ir', 'pup', 'dnr'].includes(st)) continue
      usedPlayers.add(player.name)
      // Prefer the promoted player's OWN position. Falling back to the
      // slot's position labelled a promoted guard with the outgoing
      // forward's "F". NFL depth entries carry no position, so they keep
      // using the slot's — unchanged there.
      return { position: player.position || s.position, name: player.name, shortName: player.shortName, side: s.side || null, status: status || null }
    }
    // All available players at this position are out
    return null
  }).filter(Boolean)
  const hasStarters = todayStarters.length > 0

  // NFL rosters carry a `side` on each starter (offense/defense/special) so
  // the modal can render three subsections in the canonical order fans
  // expect. Non-NFL rosters (NBA / WNBA starting 5) don't set `side`, so
  // we fall through to a flat list without a subheader.
  const groupedBySide = hasStarters && todayStarters.some((s) => s.side)
  const sides = groupedBySide
    ? [
        { key: 'offense', label: 'Offense', rows: todayStarters.filter((s) => s.side === 'offense') },
        { key: 'defense', label: 'Defense', rows: todayStarters.filter((s) => s.side === 'defense') },
        { key: 'special', label: 'Special Teams', rows: todayStarters.filter((s) => s.side === 'special') },
      ].filter((g) => g.rows.length)
    : null

  return (
    <div className="min-w-0">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-1 h-5 rounded-full shrink-0" style={{ backgroundColor: TEAM_COLORS[teamName] || '#888' }} />
        <h3 className="font-display text-base truncate">{teamName}</h3>
      </div>

      {hasStarters && (
        <div className="mb-4">
          {/* Football is picked days ahead and what ESPN returns is a depth
              chart, not a lineup card — "Today's Starters" read wrong on a
              Thursday for a Sunday game. Matched on the prefix rather than
              an explicit list, which would already have missed
              americanfootball_nfl_preseason alongside ncaaf and ufl. Other
              sports here (NBA / WNBA starting five) really are day-of, so
              they keep the original wording. */}
          <div className="text-xs text-text-muted uppercase tracking-wider mb-2">
            {String(sportKey || '').startsWith('americanfootball') ? 'Starting Roster' : "Today's Starters"}
          </div>
          {groupedBySide ? (
            <div className="space-y-3">
              {sides.map((g) => (
                <div key={g.key}>
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-text-muted mb-1">{g.label}</div>
                  <div className="space-y-1.5">
                    {/* Index in the key: a unit can legitimately field two
                        rows at the same position (WR1 / WR2), so position
                        alone is no longer unique. */}
                    {g.rows.map((s, i) => (
                      <div key={`${g.key}-${s.position}-${i}`} className="flex items-center gap-2 text-sm">
                        <span className="font-semibold text-accent w-9 shrink-0">{s.position}</span>
                        <span className="text-text-primary truncate">{s.shortName}</span>
                        <InjuryBadge status={s.status} className="text-[10px]" />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-1.5">
              {todayStarters.map((s, i) => (
                <div key={`${s.position}-${i}`} className="flex items-center gap-2 text-sm">
                  <span className="font-semibold text-accent w-7 shrink-0">{s.position}</span>
                  <span className="text-text-primary truncate">{s.shortName}</span>
                  <InjuryBadge status={s.status} className="text-[10px]" />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div>
        <div className="text-xs text-text-muted uppercase tracking-wider mb-2">Injuries</div>
        {hasInjuries ? (
          <div className="space-y-1.5">
            {injuries.map((inj) => (
              <InjuryRow key={inj.name} inj={inj} />
            ))}
          </div>
        ) : (
          <p className="text-sm text-correct">No injuries reported</p>
        )}
      </div>
    </div>
  )
}

export default function GameIntelModal({ gameId, onClose }) {
  const { data, isLoading } = useInjuryDetail(gameId)

  // Probable starters come from ESPN's SCOREBOARD payload, which this
  // modal's endpoint doesn't touch — hence the second request. Gated to MLB
  // so no other sport pays for a call that would return nothing. This block
  // used to live in GameDetailModal (since deleted); when the Picks card
  // stopped opening that modal, MLB quietly lost its probable pitchers.
  // Also the only source of team RECORDS for sports outside INJURY_SPORTS:
  // those take the injuries route's early-return branch, which never computes
  // them, which is why MLB showed no record under the logos.
  const needsIntel = !!data && (data.sportKey === 'baseball_mlb' || (!data.homeRecord && !data.awayRecord))
  const { data: intel } = useGameIntel(needsIntel ? gameId : null)
  const awayRecord = data?.awayRecord || intel?.awayRecord
  const homeRecord = data?.homeRecord || intel?.homeRecord

  // True when this sport actually has lineup/injury coverage. Sports
  // outside INJURY_SPORTS (NCAAF, MLS, NCAAB) come back with empty
  // rosters, and printing "No injuries reported" for those would assert
  // something we never checked.
  const hasIntel = !!(
    data?.home?.starters?.length || data?.home?.injuries?.length ||
    data?.away?.starters?.length || data?.away?.injuries?.length
  )

  useEffect(() => {
    if (!gameId) return
    lockScroll()
    return () => unlockScroll()
  }, [gameId])

  if (!gameId) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      style={{
        paddingTop: 'max(1.5rem, calc(3.5rem + env(safe-area-inset-top) + 1rem))',
        paddingBottom: 'max(1.5rem, calc(3.5rem + env(safe-area-inset-bottom) + 1rem))',
      }}
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/60" />
      {/* Width matches GameCenterModal (sm:max-w-3xl) so the two game modals
          are the same object at the same size. Was md:max-w-lg — half the
          width, and switching a breakpoint later, so on a 700px-wide window
          Game Center was already wide while this was still phone-sized. */}
      <div
        className="relative bg-bg-primary/90 backdrop-blur-md border border-text-primary/20 w-full sm:max-w-3xl rounded-2xl p-4 sm:p-6 max-h-full overflow-y-auto overscroll-contain"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-2 right-2 w-10 h-10 flex items-center justify-center text-text-muted hover:text-text-primary text-xl leading-none rounded-full hover:bg-bg-secondary transition-colors"
        >
          &times;
        </button>

        <h2 className="font-display text-lg mb-4">Game Intel</h2>

        {isLoading ? (
          <LoadingSpinner />
        ) : !data ? (
          <p className="text-text-muted text-center">No data available</p>
        ) : (
          <>
            {/* Team logos + records. Logos always show so the modal has
                identity even during preseason / offseason when records
                are null. Record + L10 lines render only when present. */}
            {/* Capped and centered. Left to flex-1 in a 768px shell the two
                logos drift to opposite edges with a lonely "vs" marooned in
                the middle — the matchup stops reading as a matchup. Same
                max-w-md + mx-auto treatment Game Center gives its strips. */}
            <div className="flex items-center justify-between mb-4 px-2 w-full max-w-md mx-auto">
              <div className="text-center flex-1">
                <TeamLogo team={data.away_team} sportKey={data.sportKey} />
                {awayRecord && <div className="text-sm font-bold text-text-primary">{awayRecord}</div>}
                {data.awayLast10 && <div className="text-[10px] text-text-muted">L10: {data.awayLast10}</div>}
              </div>
              {/* "@" not "vs" — the layout is away on the left, home on the
                  right, so this reads "away @ home" exactly like the picks
                  list and Game Center. It said "vs", which told a Brewers
                  fan their road game was at home. */}
              <div className="text-xs text-text-muted font-semibold">@</div>
              <div className="text-center flex-1">
                <TeamLogo team={data.home_team} sportKey={data.sportKey} />
                {homeRecord && <div className="text-sm font-bold text-text-primary">{homeRecord}</div>}
                {data.homeLast10 && <div className="text-[10px] text-text-muted">L10: {data.homeLast10}</div>}
              </div>
            </div>

            {/* Preseason lineups are ad-hoc — starters typically play a
                series or sit entirely, and ESPN's depth chart is a
                season-level snapshot. Flag it so users don't assume a
                Q1 Burrow appearance in a preseason opener. */}
            {/* Pre-game preview — same component Game Center uses, so the two
                modals describe an upcoming game identically. Renders nothing
                when the game has started or ESPN has no preview data. */}
            {/* afterOdds puts probable pitchers ABOVE Team Leaders, inside
                the preview. showInjuries is suppressed where team_intel already
                supplies a richer injury list (NBA/WNBA/NFL/NHL). */}
            <GamePreview
              preview={data.preview}
              away={{ id: 'away', abbr: data.away_team, short: data.away_team }}
              home={{ id: 'home', abbr: data.home_team, short: data.home_team }}
              afterOdds={(
                <ProbablePitchers
                  awayPitcher={intel?.awayPitcher}
                  homePitcher={intel?.homePitcher}
                  awayTeam={data.away_team}
                  homeTeam={data.home_team}
                />
              )}
              showInjuries={!hasIntel}
            />

            {/* Gated on hasIntel too — the note describes the lineups, so it
                would be orphaned above nothing when we have no roster feed. */}
            {data.sportKey?.endsWith('_preseason') && hasIntel && (
              <div className="mb-4 px-3 text-[11px] text-yellow-400 leading-snug">
                Preseason: shown lineups reflect the season depth chart. Actual preseason usage varies by team — starters may sit or play limited snaps.
              </div>
            )}

            {/* Only render the lineup/injury columns when we actually have
                coverage for this sport. NCAAF isn't in INJURY_SPORTS, so the
                server returns empty rosters — rendering them anyway would
                print "No injuries reported", which claims we checked. */}
            {/* Grid switches at sm:, not md:. The container now widens at sm,
                so the lineup columns have to switch on the same breakpoint —
                otherwise 640-767px renders one stacked column inside a shell
                that's already twice as wide as it needs to be. */}
            {hasIntel && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <TeamSection
                teamName={data.away_team}
                starters={data.away?.starters}
                injuries={data.away?.injuries}
                sportKey={data.sportKey}
              />
              <TeamSection
                teamName={data.home_team}
                starters={data.home?.starters}
                injuries={data.home?.injuries}
                sportKey={data.sportKey}
              />
            </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
